(ns client-portal.client.ai-agent.data
  "Full-page agent specifics: the agent selected via the ?agentId query param and the
   header filter listener — on the builder routes a concept/location/date change
   re-renders the report over the new view, elsewhere filter changes reset the chat.
   The conversation itself (state, events, loaders, subs) lives in
   client.ai-agent.chat.data, shared with the report-chat panel."
  (:require [client-portal.client.ai-agent.chat.data :as chat.d]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.reports.individual.data :as reports.d]
            [clojure.string :as s]
            [shared.core.reactive :as re]
            [shared.core.util :as util]))

(def root-path [:ai-agent :chat])
(def report-builder-path (conj root-path :report-builder))
(def save-report-path (conj report-builder-path :save))
(def report-form-path (conj report-builder-path :form))
(def save-report-settings-path (conj report-builder-path :save-settings))
(def report-settings-form-path (conj report-builder-path :settings-form))
(def report-settings-snapshot-path (conj report-builder-path :settings-snapshot))
(def report-filters-snapshot-path (conj report-builder-path :filters-snapshot))
(def share-path (conj report-builder-path :share))
(def shares-root-path (conj share-path :shares))
(def share-form-path (conj share-path :form))
(def share-user-path (conj share-path :share-user))
(def unshare-user-path (conj share-path :unshare-user))
(def save-org-shares-path (conj share-path :save-org-shares))
(def org-shares-snapshot-path (conj share-path :org-shares-snapshot))
(def share-organizations-path (conj share-path :organizations))

(re/reg-event-fx
  :ai-agent/init re/middleware
  (fn [{:keys [db]}]
    ;; Dispatched from the full-page agent's mount, which happens once per agent
    ;; context (see views/root-with-agent-context) — so start that agent from scratch,
    ;; dropping any previous agent's conversation and report form rather than showing
    ;; them under the new one. A session named in the URL is then loaded fresh.
    ;; Seeds the header filter state (timeframe/date range) from the URL before the
    ;; session/query loaders read it for their date params.
    {:db (-> db
             (chat.d/clear-chat-state)
             (update-in report-builder-path select-keys [:guide])
             (core.d/initialize-common-filters))
     :forward-events [{:register :vi-ai-agent-listener
                       :events #{:concepts/select :locations/select :locations/clear-selected
                                 :core/select-crew-type :voice-insights/select-day-part
                                 :core/select-timeframe :core/timeframe-date-range-changed}
                       :dispatch-to [:ai-agent/filters-changed]}
                      ;; Every navigation, so a route that stops naming the loaded session
                      ;; drops it — including the ones no chat event of ours ran for (a nav
                      ;; click back to an agent's landing route, a browser back).
                      {:register :ai-agent-route-listener
                       :events #{:router/set-current-route}
                       :dispatch-to [:ai-agent/sync-session-with-route]}]}))

(re/reg-event-db
  :ai-agent/start-new-report re/middleware
  (fn [db]
    (re/dispatch [:router/go-with-merged-query-params
                  {:agentId nil}
                  {:new-route [:reports/new]}])
    (-> db
        (chat.d/clear-chat-state)
        (update-in report-builder-path select-keys [:guide]))))

(re/reg-sub
  :ai-agent/id-
  :<- [:router/current-query-params-]
  (fn [qps]
    (:agent-id (util/->kebab-case-keywords qps))))

;; The active agent — resolved by kind on the routes dedicated to one (report builder,
;; Voice Insights summary), else the one picked via the ?agentId query param, resolved
;; from the loaded list.
(re/reg-sub
  :ai-agent/selected-agent-
  :<- [:core/ai-agent-list-]
  :<- [:ai-agent/id-]
  :<- [:router/current-route-]
  (fn [[agents agent-id [route-name]]]
    (cond
      (contains? chat.d/report-builder-routes route-name)
      (chat.d/find-report-builder-agent agents)

      (contains? chat.d/vi-summary-agent-routes route-name)
      (chat.d/find-vi-summary-agent agents)

      agent-id
      (some #(when (= (:public-id %) agent-id) %)
            (util/when-not-loading agents)))))

(re/reg-sub
  :ai-agent/report-builder-agent?-
  :<- [:ai-agent/selected-agent-]
  (fn [agent]
    (= (get-in agent [:configuration :kind]) "report-builder")))

;; Layered on the load-sub so derefing these (e.g. to gate the New Report and Voice
;; Insights Agent nav options) triggers the agent list fetch.
(re/reg-sub
  :ai-agent/report-builder-agent-
  :<- [:core/ai-agent-list-]
  chat.d/find-report-builder-agent)

(re/reg-sub
  :ai-agent/vi-summary-agent-
  :<- [:core/ai-agent-list-]
  chat.d/find-vi-summary-agent)

;; Seeded from the latest generated report's message (`form` holds :session-id,
;; :message-id, :title, and — for edit sessions — :description); the user can then
;; edit the title/description inputs (backed by `report-form-path`) before saving.
;; The form is scoped to its session, and a title the user has changed (it differs
;; from the last seeded one) is never overwritten by a later turn's seed — only an
;; untouched title follows the model's.
(re/reg-event-db
  :ai-agent/seed-report-form re/middleware
  (fn [db [{:keys [session-id title] :as form}]]
    (update-in db report-form-path
               (fn [cur]
                 (let [same-session? (and session-id (= session-id (:session-id cur)))
                       user-title? (and same-session?
                                        (not-empty (:title cur))
                                        (not= (:title cur) (:seeded-title cur)))]
                   (merge (when same-session? cur)
                          (cond-> form
                                  user-title? (dissoc :title))
                          {:seeded-title title}))))))

(re/reg-sub
  :ai-agent/report-form-
  (fn [db]
    (get-in db report-form-path)))

(re/reg-auth0-loader
  :ai-agent/save-report
  save-report-path
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          session-id (chat.d/session-id db)
          saved-report (->> (chat.d/session-messages db)
                            (filter :report)
                            (last)
                            :saved-report)
          {:keys [title description]} (get-in db report-form-path)
          title (or (not-empty title) (:title saved-report))
          configuration (:configuration saved-report)
          ;; Settings picked before the report ever existed ride along on the first
          ;; save (the creation); once saved they go through the settings endpoint.
          target-concept-ids (when (empty? (:report-public-id (chat.d/session db)))
                               (not-empty (vec (:target-concept-ids (get-in db report-settings-form-path)))))]
      (when (and concept-id session-id (not-empty title) (not-empty configuration))
        {:method :post
         :url "/api/reports/save"
         :json-params (cond-> {:concept-id concept-id
                               :session-id session-id
                               :title title
                               :description (not-empty description)
                               :configuration configuration}
                        target-concept-ids
                        (assoc :target-concept-ids target-concept-ids))})))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :on-success (fn [db {:keys [body]}]
                (re/dispatch [:toasts/add-info {:msg "Report saved" :duration 3000}])
                (let [public-id (:public-id (util/->kebab-case-keywords body))]
                  ;; The first save of a new report creates it — move to its edit route.
                  ;; The builder view is the same on both routes, but the route change
                  ;; still re-renders the page and resets the report + chat scroll
                  ;; containers — capture their positions and put them back after the
                  ;; new route renders.
                  (when (chat.d/new-report-route? db)
                    (let [scroll-ids ["core-scroll-content" chat.d/chat-panel-scroll-container-id]
                          scroll-tops (->> scroll-ids
                                           (keep #(when-let [el (js/document.getElementById %)]
                                                    [% (.-scrollTop el)]))
                                           (vec))
                          restore! (fn []
                                     (doseq [[id top] scroll-tops]
                                       (when-let [el (js/document.getElementById id)]
                                         (set! (.-scrollTop el) top))))]
                      (re/dispatch [:router/go-with-merged-query-params
                                    {}
                                    {:new-route [:reports/individual-edit {:report-id public-id
                                                                           :session-id (chat.d/session-id db)}]
                                     :on-route-change (fn []
                                                        (js/requestAnimationFrame
                                                          #(js/requestAnimationFrame restore!))
                                                        (util/wait 150 restore!))}])))
                  (assoc-in db (conj chat.d/session-path :report-public-id) public-id)))
  :on-fail (fn [db]
             (re/dispatch [:toasts/add-error {:msg "Failed to save report" :duration 3000}])
             db))

(re/reg-sub
  :ai-agent/saving-report?-
  (fn [db]
    (util/loading? (get-in db save-report-path))))

(re/reg-sub
  :ai-agent/saved-report-public-id-
  (fn [db]
    (:report-public-id (chat.d/session db))))

;; Seed the settings form from the linked report's stored settings when a session
;; loads (dispatched by the session loader in chat.data, which doesn't know the
;; builder's paths) — but never over selections already made this session.
(re/reg-event-db
  :ai-agent/seed-report-settings re/middleware
  (fn [db [target-concept-ids]]
    (if (and (some? target-concept-ids)
             (nil? (get-in db (conj report-settings-form-path :target-concept-ids))))
      (assoc-in db (conj report-settings-form-path :target-concept-ids) (vec target-concept-ids))
      db)))

;; Open the settings dialog keeping any selections already in the form — before the
;; first save they're stashed here and applied on report creation, so a reopen (or
;; the save itself) must still see them. The selections as of opening are snapshotted
;; so a cancel/close can put them back.
(re/reg-event-db
  :ai-agent/open-report-settings re/middleware
  (fn [db]
    (re/dispatch [:dialog/show [:report/settings-dialog]])
    (let [db (-> db
                 (update-in (conj report-settings-form-path :target-concept-ids) #(or % []))
                 (assoc-in (conj share-form-path :email-address) "")
                 ;; Seeded from the report every open: the config is the source of truth
                 ;; and a turn since the last open may have changed it.
                 (update-in report-settings-form-path merge (or (chat.d/report-filters db)
                                                                (reports.d/report-filters nil))))]
      (-> db
          (assoc-in report-settings-snapshot-path
                    (get-in db (conj report-settings-form-path :target-concept-ids)))
          (assoc-in report-filters-snapshot-path
                    (select-keys (get-in db report-settings-form-path)
                                 (map :key reports.d/report-filter-options)))))))

;; Cancel/close (vs Save/Update): revert the selections to the opening snapshot.
(re/reg-event-db
  :ai-agent/cancel-report-settings re/middleware
  (fn [db]
    (re/dispatch [:dialog/hide])
    (-> db
        (assoc-in (conj report-settings-form-path :target-concept-ids)
                  (get-in db report-settings-snapshot-path []))
        (assoc-in (conj report-settings-form-path :tenant-organization-ids)
                  (get-in db org-shares-snapshot-path []))
        (update-in report-settings-form-path merge (get-in db report-filters-snapshot-path {})))))

(defn- settings-form-filters [db]
  (let [form (get-in db report-settings-form-path)]
    (->> reports.d/report-filter-options
         (map (fn [{:keys [key]}] [key (boolean (get form key))]))
         (into {}))))

(defn- with-in-session-filters
  "Write the saved filters into the builder's in-session report configuration. The
   server has them, but a Save Report posts the configuration held here — without this
   the next save would put the old ones back."
  [db filters]
  (let [messages (chat.d/session-messages db)
        idx (some->> messages
                     (map-indexed vector)
                     (filter (comp :report second))
                     (last)
                     (first))
        config (some-> (get-in messages [idx :saved-report :configuration])
                       (util/json-string->map-basic))]
    (if (nil? config)
      db
      (->> (reduce (fn [acc {:keys [key config-key]}]
                     (assoc acc config-key (boolean (get filters key))))
                   (get config "filters" {})
                   reports.d/report-filter-options)
           (assoc config "filters")
           (util/clj->json-string)
           (assoc-in db (conj chat.d/session-path :messages idx :saved-report :configuration))))))

(re/reg-auth0-loader
  :ai-agent/save-report-settings
  save-report-settings-path
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          report-public-id (:report-public-id (chat.d/session db))
          {:keys [target-concept-ids]} (get-in db report-settings-form-path)]
      (when (and concept-id (not-empty report-public-id))
        {:method :post
         :url (str "/api/reports/i/" report-public-id "/settings")
         ;; `session-id` so the server can carry the filters into the builder session's
         ;; working spec — otherwise the next turn regenerates the report without them.
         :json-params {:concept-id concept-id
                       :session-id (chat.d/session-id db)
                       :target-concept-ids (vec target-concept-ids)
                       :filters (settings-form-filters db)}})))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :on-success (fn [db {:keys [body]}]
                (re/dispatch [:toasts/add-info {:msg "Report settings saved" :duration 3000}])
                (re/dispatch [:dialog/hide])
                (let [filters (settings-form-filters db)
                      report-id (chat.d/report-public-id db)
                      ;; Hiding a filter drops its value from the report's query, so the
                      ;; report it was showing is now stale — re-render it the same way a
                      ;; header filter change does.
                      changed? (not= filters (get-in db report-filters-snapshot-path))]
                  (when (and changed? (chat.d/session-id db))
                    (re/dispatch-once [:ai-agent/load-session :reload]))
                  (cond-> (with-in-session-filters db filters)
                          true (assoc-in report-filters-snapshot-path filters)
                          ;; Written rather than invalidated: the reload below reads the
                          ;; report's filters from here to strip its own params, so a
                          ;; refetch window would leave that one request unstripped.
                          report-id (assoc-in (conj reports.d/settings-by-id-path report-id)
                                              (select-keys (util/->kebab-case-keywords body)
                                                           [:target-concept-ids :filters]))
                          changed? (assoc-in chat.d/session-reloading-path true))))
  :on-fail (fn [db]
             (re/dispatch [:toasts/add-error {:msg "Failed to save report settings" :duration 3000}])
             db))

(re/reg-event-db
  :ai-agent/apply-report-settings re/middleware
  (fn [db]
    ;; Before the first save there is no report to write to, so the selections ride along
    ;; on creation instead — concepts through the save request, filters through the
    ;; configuration it posts.
    (re/dispatch [:dialog/hide])
    (with-in-session-filters db (settings-form-filters db))))

(re/reg-sub
  :ai-agent/saving-report-settings?-
  (fn [db]
    (util/loading? (get-in db save-report-settings-path))))

(re/reg-sub
  :ai-agent/report-settings-target-concept-ids-
  (fn [db]
    (get-in db (conj report-settings-form-path :target-concept-ids))))

;; The filter visibility the builder's current report declares — what the report layout
;; and the load params below both read.
(re/reg-sub
  :ai-agent/builder-report-filters-
  (fn [db]
    (chat.d/report-filters db)))

(re/reg-sub
  :ai-agent/builder-report-public-id-
  (fn [db]
    (chat.d/report-public-id db)))

;; The concepts the report is limited to, so the builder edits it under a view its
;; readers can actually get.
(re/reg-sub
  :ai-agent/builder-report-target-concept-ids-
  (fn [db]
    (some->> (chat.d/report-public-id db)
             (reports.d/settings db)
             (:target-concept-ids)
             (not-empty))))

;; Sharing. Only reachable once the report has been saved (there is nothing to share
;; before that), so every request below can count on a report public id. Direct-by-email
;; shares are written the moment they are submitted; organization shares are collected in
;; the settings form and written by the dialog's Save button.
(defn- share-report-public-id [db]
  (:report-public-id (chat.d/session db)))

;; Keyed by report public id so moving between reports never reads another's shares. The
;; loader, its load-sub and the response handler all hand the db in as the last argument.
(defn shares-path [& args]
  (conj shares-root-path (str (share-report-public-id (last args)))))

(defn- update-shares
  "Update the cached shares response, ignoring the load's own `:loading` placeholder."
  [db f]
  (cond-> db
          (map? (get-in db (shares-path db))) (update-in (shares-path db) f)))

(re/reg-auth0-loader
  :ai-agent/load-report-shares
  shares-path
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          report-public-id (share-report-public-id db)]
      (when (and concept-id (not-empty report-public-id))
        {:method :get
         :url (str "/api/reports/i/" report-public-id "/shares")
         :query-params {:concept-id concept-id}})))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  ;; The response is the authority on the organization selections, so it seeds both the
  ;; form and the snapshot a cancel reverts to — the dialog can be opened before it lands.
  :on-success (fn [db {:keys [body]}]
                (let [ids (-> body (util/->kebab-case-keywords) :tenant-organization-ids (vec))]
                  (-> db
                      (assoc-in (conj report-settings-form-path :tenant-organization-ids) ids)
                      (assoc-in org-shares-snapshot-path ids)))))

(re/reg-auth0-load-sub
  :ai-agent/report-shares-
  shares-path
  :ai-agent/load-report-shares)

(re/reg-auth0-loader
  :ai-agent/load-share-organizations
  share-organizations-path
  (fn [db]
    (when-let [concept-id (concepts.d/current-concept-id db)]
      {:method :get
       :url "/api/reports/share-organizations"
       :query-params {:concept-id concept-id}}))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :ai-agent/share-organizations-
  share-organizations-path
  :ai-agent/load-share-organizations)

(defn- share-email-address [db]
  (some-> (get-in db (conj share-form-path :email-address)) (s/trim)))

(re/auth0-http-request
  :ai-agent/share-report-with-user
  share-user-path
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          report-public-id (share-report-public-id db)
          email-address (share-email-address db)]
      (when (and concept-id (not-empty report-public-id) (util/valid-email-address? email-address))
        {:method :post
         :url (str "/api/reports/i/" report-public-id "/shares/user")
         :json-params {:concept-id concept-id
                       :email-address email-address}})))
  :on-success (fn [db [{:keys [body]}]]
                (re/dispatch [:toasts/add-info {:msg "Report shared" :duration 3000}])
                (re/dispatch [:form/change share-form-path :email-address ""])
                (update-shares db (fn [shares]
                                    (update shares :users
                                            (fn [users]
                                              ;; Replace rather than append, so re-sharing
                                              ;; with someone already listed does not
                                              ;; double them up. Owner stays on top.
                                              (->> users
                                                   (remove #(= (:user-id %) (:user-id body)))
                                                   (cons (select-keys body [:user-id :email-address]))
                                                   (sort-by (juxt (comp not :is-owner) :email-address))
                                                   (vec)))))))
  ;; The input is left as-is on a failure so a typo can be corrected in place.
  :on-fail (fn [db {:keys [body]}]
             (re/dispatch [:toasts/add-error {:msg (or (not-empty (:error body)) "Failed to share report")
                                              :duration 5000}])
             db))

(re/auth0-http-request
  :ai-agent/unshare-report-with-user
  unshare-user-path
  (fn [db user-id]
    (let [concept-id (concepts.d/current-concept-id db)
          report-public-id (share-report-public-id db)]
      (when (and concept-id (not-empty report-public-id) user-id)
        {:method :delete
         :url (str "/api/reports/i/" report-public-id "/shares/user")
         :json-params {:concept-id concept-id
                       :user-id user-id}})))
  :on-success (fn [db [{:keys [body]}]]
                (update-shares db (fn [shares]
                                    (update shares :users
                                            (fn [users]
                                              (->> users
                                                   (remove #(= (:user-id %) (:user-id body)))
                                                   (vec)))))))
  :on-fail (fn [db]
             (re/dispatch [:toasts/add-error {:msg "Failed to remove share" :duration 3000}])
             db))

(re/auth0-http-request
  :ai-agent/save-report-org-shares
  save-org-shares-path
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          report-public-id (share-report-public-id db)]
      (when (and concept-id (not-empty report-public-id))
        {:method :post
         :url (str "/api/reports/i/" report-public-id "/shares/organizations")
         :json-params {:concept-id concept-id
                       :tenant-organization-ids (vec (get-in db (conj report-settings-form-path
                                                                      :tenant-organization-ids)))}})))
  ;; The concept settings save runs next so the dialog gets one toast and one close for
  ;; the whole Save, whichever parts of it were dirty.
  :on-success (fn [db [{:keys [body]}]]
                (re/dispatch [:ai-agent/save-report-settings])
                (let [ids (vec (:tenant-organization-ids body))]
                  (-> db
                      (assoc-in (conj report-settings-form-path :tenant-organization-ids) ids)
                      (assoc-in org-shares-snapshot-path ids)
                      (update-shares #(assoc % :tenant-organization-ids ids)))))
  :on-fail (fn [db]
             (re/dispatch [:toasts/add-error {:msg "Failed to save organization shares" :duration 3000}])
             db))

(re/reg-event-db
  :ai-agent/save-report-settings+shares re/middleware
  (fn [db]
    (let [org-ids (set (get-in db (conj report-settings-form-path :tenant-organization-ids)))]
      (if (not= org-ids (set (get-in db org-shares-snapshot-path)))
        (re/dispatch [:ai-agent/save-report-org-shares])
        (re/dispatch [:ai-agent/save-report-settings])))
    db))

(re/reg-sub
  :ai-agent/report-share-users-
  (fn [db]
    (get-in db (conj (shares-path db) :users))))


(re/reg-sub
  :ai-agent/sharing-report?-
  (fn [db]
    (boolean (get-in db (conj share-user-path :request-active?)))))

(re/reg-sub
  :ai-agent/saving-org-shares?-
  (fn [db]
    (boolean (get-in db (conj save-org-shares-path :request-active?)))))
